import rawRegistry from './surfaceTelemetryRegistry.json';

/** Closed parent-facing vocabulary for Study time themes.
 *
 * Themes are DECLARED per surface at registration (see `parentTheme` below),
 * never inferred from the shape of a surface key. `scripts/gen_family_parent_themes.py`
 * projects these declarations into `backend/services/family_parent_themes.json`,
 * which `services.family_activity_service.parent_theme()` reads as an exact
 * lookup. "Other Pinpoint activity" is a deliberate label a surface may choose
 * (e.g. account management time); it is also the honest fallback the backend
 * uses for historical keys of retired surfaces that are no longer registered.
 */
export const PARENT_THEMES = [
  'Practice',
  'Practice test',
  'Vocabulary',
  'Lessons & reference',
  'Progress & planning',
  'Choosing a test',
  'Other Pinpoint activity',
] as const;

export type ParentTheme = (typeof PARENT_THEMES)[number];

export type SurfaceTelemetryVariant = {
  when: string;
  surfaceKey: string;
  /** Required on measured variants. */
  parentTheme?: ParentTheme;
};

export type SurfaceTelemetryEntry = {
  routePath: string;
  surfaceKey?: string;
  /** Required on `measured_active` entries that declare a `surfaceKey`. */
  parentTheme?: ParentTheme;
  variants?: SurfaceTelemetryVariant[];
  declaredEvents?: string[];
  durationMode: 'measured_active' | 'not_measured';
  reason?: string;
  owner?: string;
  expiresOn?: string;
};

export const BASE_SURFACE_TELEMETRY_REGISTRY: readonly SurfaceTelemetryEntry[] =
  rawRegistry.entries as SurfaceTelemetryEntry[];

export const FAMILY_SURFACE_TELEMETRY_REGISTRY: readonly SurfaceTelemetryEntry[] = [
  {
    routePath: '/choose-role',
    surfaceKey: 'choose_role',
    durationMode: 'not_measured',
    reason: 'One-time account-type chooser, not Study time; write-once and not re-visited.',
    owner: 'Product',
    expiresOn: '2026-11-30',
  },
  ...[
    ['/family', 'family_overview'],
    ['/family/activity', 'family_activity'],
    ['/family/insights', 'family_insights'],
    ['/family/plan', 'family_plan'],
    ['/family/habits', 'family_habits'],
    ['/family/reports/:reportKey', 'family_report'],
    ['/family/connections', 'family_connections'],
    ['/family/settings', 'family_settings'],
  ].map(([routePath, surfaceKey]) => ({
    routePath,
    surfaceKey,
    durationMode: 'not_measured' as const,
    reason: 'Guardian / Parents is a relationship-authorized viewing surface, not student Study time.',
    owner: 'Product',
    expiresOn: '2026-11-30',
  })),
];

/** Family and first-run chooser telemetry are production-bound and explicitly
 * excluded from Study-time measurement. */
export const SURFACE_TELEMETRY_REGISTRY: readonly SurfaceTelemetryEntry[] = [
  ...BASE_SURFACE_TELEMETRY_REGISTRY,
  ...FAMILY_SURFACE_TELEMETRY_REGISTRY,
];

function matchesRoute(pathname: string, routePath: string): boolean {
  if (!routePath.includes('/:')) return pathname === routePath;
  const prefix = routePath.slice(0, routePath.indexOf('/:') + 1);
  return pathname.startsWith(prefix) && pathname.slice(prefix.length).length > 0
    && !pathname.slice(prefix.length).includes('/');
}
export function resolveSurfaceTelemetry(
  pathname: string,
  search = '',
): SurfaceTelemetryEntry | null {
  const normalized = pathname === '/' ? pathname : pathname.replace(/\/+$/, '');
  const entry = SURFACE_TELEMETRY_REGISTRY.find(candidate =>
    matchesRoute(normalized, candidate.routePath));
  if (!entry || entry.durationMode !== 'measured_active') return null;
  if (!entry.variants?.length) return entry;
  const hasWord = new URLSearchParams(search).has('word');
  const variant = entry.variants.find(candidate =>
    candidate.when === (hasWord ? 'word query parameter is present' : 'word query parameter is absent'));
  return variant
    ? {
      ...entry,
      surfaceKey: variant.surfaceKey,
      parentTheme: variant.parentTheme,
      variants: undefined,
    }
    : null;
}
